import { memo, useCallback } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'services/Translation'
import Typography from 'components/common/Typography'
import Separator from 'components/common/Separator'
import DefaultBtn from 'components/common/buttons/Default'
import { textPrimary, greySecondary, purple } from 'styles/Colors'
import { CS, CS_m } from 'back-end-api'
import { hp } from 'styles/Sizes'
import Copy from 'assets/icons/Copy'
import Edit from 'assets/icons/Edit'
import useDoctorMap from 'hooks/useDoctorMap'
import { ModalContainer } from '../../Common'
import { AdviceStatus } from '../common'

const AdviceContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 20px;
`

const ResizableContainer = styled.div`
  width: 100%;
  min-height: 200px;
  max-height: 400px;
  padding: 12px;
  padding-top: 40px; /* Make room for the copy icon */
  border: 1px solid ${greySecondary};
  border-radius: 8px;
  font-size: 16px;
  font-family: coresanscr45;
  line-height: 1.5;
  background: #f9f9f9;
  color: ${textPrimary};
  overflow-y: auto;
  
  /* Style the HTML content */
  p {
    margin: 0 0 0.25em 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong, b {
    font-weight: bold;
  }
  
  em, i {
    font-style: italic;
  }
  
  u {
    text-decoration: underline;
  }
  
  ul, ol {
    margin: 0 0 1em 0;
    padding-left: 2em;
  }
  
  ul {
    list-style-type: disc;
  }
  
  ol {
    list-style-type: decimal;
  }
  
  li {
    margin-bottom: 0.5em;
    display: list-item;
  }
  
  h1, h2, h3, h4, h5, h6 {
    margin: 0 0 0.5em 0;
    font-weight: bold;
  }
  
  h1 { font-size: 1.5em; }
  h2 { font-size: 1.3em; }
  h3 { font-size: 1.1em; }
`

const CopyButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  z-index: 1;
  
  &:hover {
    background-color: #EB7C0C20;
  }
  
  &:active {
    background-color: #EB7C0C30;
  }
`

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid ${greySecondary};
`

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    btnLabelClose: getText('Close', 'Patient_Details_Modal_Advice'),
    adviceHistoryTitle: getText('Advice History', 'Patient_Details_Modal_Advice'),
    currentAdviceTitle: getText('Current Advice', 'Patient_Details_Modal_Advice'),
    createNewAdviceBtn: getText('Create New Advice', 'Patient_Details_Modal_Advice'),
    noAdviceText: getText('No advice has been sent yet', 'Patient_Details_Modal_Advice'),
    sentBy: getText('Sent by Dr. %s', 'Patient_Details_Modal_Advice'),
  }[key])

interface AdviceHistoryProps {
  onClose: () => void
  registration: CS_m.ModelsRegistration | null
  setStatus: (status: AdviceStatus) => void
}

const AdviceHistory = ({ onClose, registration, setStatus }: AdviceHistoryProps) => {
  const { t } = useTranslation(texts)
  const doctorMap = useDoctorMap()
  const summary = CS.getSummary(registration)
  const currentAdvice = summary?.advice?.text
  const adviceSenderId = summary?.advice?.doctor_account_id ?? summary?.advice?.author_account_id
  const adviceSenderName = adviceSenderId ? doctorMap.get(adviceSenderId) : undefined

  const handleCopyAdvice = useCallback(async () => {
    if (currentAdvice) {
      try {
        // Copy with HTML formatting preserved
        const htmlBlob = new Blob([currentAdvice], { type: 'text/html' })
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = currentAdvice
        const plainText = tempDiv.textContent || tempDiv.innerText || ''
        const textBlob = new Blob([plainText], { type: 'text/plain' })
        await navigator.clipboard.write([
          new window.ClipboardItem({
            'text/html': htmlBlob,
            'text/plain': textBlob,
          }),
        ])
      } catch (err) {
        console.error('Failed to copy advice:', err)
        // Fallback for older browsers
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = currentAdvice
        const plainText = tempDiv.textContent || tempDiv.innerText || ''
        
        const textArea = document.createElement('textarea')
        textArea.value = plainText
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
    }
  }, [currentAdvice])

  const handleCreateNewAdvice = useCallback(() => {
    setStatus(AdviceStatus.WriteAdvice)
  }, [setStatus])

  return (
    <ModalContainer onClose={onClose} label={t('btnLabelClose')}>
      <Typography
        type="title"
        className="font-csc65"
        text={t('adviceHistoryTitle')}
        color={textPrimary}
        size={18}
        style={undefined}
      />
      <Separator size={hp(3)} />
      
      {currentAdvice ? (
        <>
          <Typography
            type="title"
            className="font-csc45"
            text={t('currentAdviceTitle')}
            color={textPrimary}
            size={16}
            style={undefined}
          />
          {adviceSenderName && (
            <>
              <Separator size={hp(0.8)} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: purple }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span className="font-csc45" style={{ fontSize: 13, fontStyle: 'italic' }}>
                  {t('sentBy').replace('%s', adviceSenderName)}
                </span>
              </div>
            </>
          )}
          <Separator size={hp(2)} />
          <AdviceContainer>
            <CopyButton onClick={handleCopyAdvice} title="Copy advice">
              <Copy className="" color="#EB7C0C" />
            </CopyButton>
            <ResizableContainer
              dangerouslySetInnerHTML={{
                __html: currentAdvice
              }}
            />
          </AdviceContainer>
        </>
      ) : (
        <>
          <Typography
            className="font-csc45"
            text={t('noAdviceText')}
            color={greySecondary}
            size={16}
            style={{ textAlign: 'center', fontStyle: 'italic' }}
          />
          <Separator size={hp(3)} />
        </>
      )}

      <ActionButtonsContainer>
        <DefaultBtn
          label={t('createNewAdviceBtn')}
          onClick={handleCreateNewAdvice}
          className="font-csc65"
          style={{ 
            backgroundColor: purple,
            opacity: CS.isSummaryStatusFinal(summary) ? 0.5 : 1,
            cursor: CS.isSummaryStatusFinal(summary) ? 'not-allowed' : 'pointer'
          }}
          color="white"
          previousIcon={<Edit className="" color="white" /> as any}
          nextIcon={undefined}
          disabled={CS.isSummaryStatusFinal(summary)}
        />
      </ActionButtonsContainer>
    </ModalContainer>
  )
}

AdviceHistory.whyDidYouRender = true

export default memo(AdviceHistory)